"use client";

import { useRef, useState } from "react";
import { devLabel } from "@/lib/dev-labels";

/**
 * MulticamMode — Phase 25 Increment 15.
 *
 * Before this it was a static UI mockup: `slots` was a hardcoded const,
 * the slot tiles had `cursor:pointer` but no onClick, there were no file
 * inputs, and the "멀티캠 분석" button fired an empty `onAnalyze()`.
 *
 * Now each of the 6 slots is a real file picker; the picked camera files
 * are tracked in state and passed to `onAnalyze(files)` so WelcomePage can
 * create one analysis job per camera (same backend as the file-upload flow).
 */

interface CamSlot {
	id: number;
	file: File | null;
}

const SLOT_COUNT = 6;
const INITIAL: CamSlot[] = Array.from({ length: SLOT_COUNT }, (_, i) => ({
	id: i + 1,
	file: null,
}));

export interface MulticamModeProps {
	/** Passes every picked camera File (Increment 15 — was an empty call). */
	onAnalyze?: (files: File[]) => void;
}

export function MulticamMode({ onAnalyze }: MulticamModeProps) {
	const [slots, setSlots] = useState<CamSlot[]>(INITIAL);
	const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});
	const pickedFiles = slots
		.map((s) => s.file)
		.filter((f): f is File => f !== null);
	const activeCount = pickedFiles.length;

	function pickFile(slotId: number, file: File | null | undefined) {
		if (!file) return;
		setSlots((prev) =>
			prev.map((s) => (s.id === slotId ? { ...s, file } : s)),
		);
	}

	return (
		<div {...devLabel("11-A5")}>
			<div style={{ fontSize: 10, opacity: 0.65, marginBottom: 7 }}>
				여러 카메라 동시 편집 (최대 6개 · 클릭해서 영상 파일 선택)
			</div>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(3,1fr)",
					gap: 5,
				}}
			>
				{slots.map((s) => {
					const active = s.file !== null;
					return (
						<div
							key={s.id}
							role="button"
							tabIndex={0}
							aria-label={`카메라 ${s.id} 파일 선택`}
							onClick={() => inputRefs.current[s.id]?.click()}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ")
									inputRefs.current[s.id]?.click();
							}}
							style={{
								aspectRatio: "9/16",
								padding: 5,
								borderRadius: 5,
								border: active
									? "1.5px solid #d4af37"
									: "1.5px dashed rgba(255,255,255,0.1)",
								background: active
									? "rgba(212,175,55,0.08)"
									: "rgba(0,0,0,0.2)",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								gap: 3,
								cursor: "pointer",
								fontSize: 9,
								color: active ? "#d4af37" : "#a3a3a3",
								textAlign: "center",
								overflow: "hidden",
							}}
						>
							<input
								ref={(el) => {
									inputRefs.current[s.id] = el;
								}}
								type="file"
								accept="video/mp4,video/quicktime,video/x-matroska,video/webm,video/x-msvideo"
								style={{ display: "none" }}
								onChange={(e) => pickFile(s.id, e.target.files?.[0])}
							/>
							{active ? (
								<>
									<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<polygon points="23 7 16 12 23 17 23 7" />
										<rect x="1" y="5" width="15" height="14" rx="2" />
									</svg>
									<div>CAM {s.id}</div>
									<div
										style={{
											fontSize: 8,
											opacity: 0.6,
											maxWidth: "100%",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{s.file?.name}
									</div>
								</>
							) : (
								<>
									<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
										<line x1="12" y1="5" x2="12" y2="19" />
										<line x1="5" y1="12" x2="19" y2="12" />
									</svg>
									<span style={{ fontSize: 9 }}>CAM {s.id}</span>
								</>
							)}
						</div>
					);
				})}
			</div>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginTop: 8,
				}}
			>
				<div
					style={{
						fontSize: 9,
						opacity: 0.5,
						fontFamily: "'Geist Mono',monospace",
					}}
				>
					{activeCount} / {SLOT_COUNT} 카메라 선택됨
				</div>
				<button
					type="button"
					onClick={() => onAnalyze?.(pickedFiles)}
					disabled={activeCount === 0}
					style={{
						padding: "6px 16px",
						background: activeCount > 0 ? "#fff" : "rgba(255,255,255,0.2)",
						color: activeCount > 0 ? "#0a0a0a" : "rgba(255,255,255,0.4)",
						borderRadius: 5,
						fontWeight: 700,
						fontSize: 12,
						border: "none",
						cursor: activeCount > 0 ? "pointer" : "not-allowed",
					}}
				>
					멀티캠 분석 ({activeCount * 10}코인)
				</button>
			</div>
		</div>
	);
}
